// src/components/auth/Login.jsx
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './auth.css';
import logo from '../../assets/logo3.png';
import GoodbyePopup from './GoodbyePopup';

const Login = ({ setIsAuthenticated, setUser }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        remember: false
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Welcome back popup state
    const [showWelcomeBack, setShowWelcomeBack] = useState(false);
    const [welcomeUserName, setWelcomeUserName] = useState('');
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [redirectPath, setRedirectPath] = useState('/dashboard');
    
    // Get success message from registration redirect
    const successMessage = location.state?.message;
    
    // Use environment variable for API URL
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

      try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password,
                    remember: formData.remember
                })
            });

            const data = await response.json();

            if (response.ok) {
                // Gate: unverified users don't get into the dashboard.
                if (data.user && data.user.emailVerified === false) {
                    setError('Please verify your email first. Check your inbox for the link.');
                    setLoading(false);
                    return;
                }

                const storage = formData.remember ? localStorage : sessionStorage;
                storage.setItem('authToken', data.token);
                localStorage.setItem('userInfo', JSON.stringify(data.user));

                setIsAuthenticated(true);
                setUser(data.user);
                navigate(data.user.role === 'admin' ? '/admin/dashboard' : '/dashboard');
            } else {
                setError(data.error || 'Invalid email or password');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Network error. Please check if the server is running.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth__page">
            <div className="auth__container">
                {/* Form Side */}
                <div className="auth__box">
                    {/* Back to Home — animated pill, on the form */}
                    <Link to="/" className="auth__back-btn">
                        <i className="bx bx-arrow-back"></i>
                        <span>Back to Home</span>
                    </Link>

                    <div className="auth__header">
                        <img src={logo} alt="Logo" className="auth__logo" />
                        <h2>Welcome Back</h2>
                        <p>Sign in to your account</p>
                    </div>

                    {successMessage && (
                        <div className="auth__success" style={{ 
                            background: 'rgba(72, 187, 120, 0.1)',
                            border: '1px solid rgba(72, 187, 120, 0.3)',
                            color: '#059669',
                            padding: '1rem',
                            borderRadius: '0.75rem',
                            marginBottom: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <i className="bx bx-check-circle"></i>
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div className="auth__error">
                            <i className="bx bx-error-circle"></i>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="auth__form">
                        <div className="auth__field">
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Email Address"
                                required
                            />
                            <i className="bx bx-envelope"></i>
                        </div>

                        <div className="auth__field">
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Password"
                                required
                            />
                            <i className="bx bx-lock"></i>
                        </div>

                        <div className="auth__options">
                            <label className="auth__remember">
                                <input
                                    type="checkbox"
                                    name="remember"
                                    checked={formData.remember}
                                    onChange={handleChange}
                                />
                                <span>Remember me</span>
                            </label>
                            <Link to="/forgot-password" className="auth__forgot">
                                Forgot password?
                            </Link>
                        </div>

                        <button type="submit" className="auth__button button button--flex" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    <i className="bx bx-log-in"></i>
                                    Sign In
                                </>
                            )}
                        </button>
                    </form>

                    <div className="auth__footer">
                        <p>
                            Don't have an account?{' '}
                            <Link to="/register">Sign Up</Link>
                        </p>
                    </div>
                </div>

                {/* Side Panel */}
                <div className="auth__side">
                    <h3>Welcome to SpinoSoftBits</h3>
                    <p>Access your personalized dashboard to manage projects, view invoices, and communicate with our team.</p>
                    
                    <ul className="auth__features">
                        <li>
                            <i className="bx bx-check-circle"></i>
                            <span>Track project progress in real-time</span>
                        </li>
                        <li>
                            <i className="bx bx-check-circle"></i>
                            <span>Secure file sharing and collaboration</span>
                        </li>
                        <li>
                            <i className="bx bx-check-circle"></i>
                            <span>Automated invoicing and payments</span>
                        </li>
                        <li>
                            <i className="bx bx-check-circle"></i>
                            <span>24/7 support and regular updates</span>
                        </li>
                    </ul>
                </div>
            </div>
            
            {/* Welcome Back Popup */}
            <GoodbyePopup 
                show={showWelcomeBack}
                onClose={() => setShowWelcomeBack(false)}
                userName={welcomeUserName}
                isAdmin={isAdminUser}
                type="welcome"
            />
        </div>
    );
};

export default Login;